/* qeslint-disable */

import { font } from "./font";
import { Theme } from "../theme";
import { style } from "../utils/utils";
import { Dispatcher } from "../utils/util_dispatcher";

interface CmdMap {
    [key: string]: string;
}

export class IconButton {
    ctx: CanvasRenderingContext2D;
    dom: HTMLButtonElement;
    canvas: HTMLCanvasElement;
    size: number;
    icon?: string;
    dropshadow: boolean = false;

    private static readonly CMD_MAP: CmdMap = {
        M: "moveTo",
        L: "lineTo",
        Q: "quadraticCurveTo",
        C: "bezierCurveTo",
        Z: "closePath"
    };

    private dpr: number = 1;
    private tooltip: string;
    private dispatcher?: Dispatcher;

    constructor(size: number, icon: string | undefined, tooltip: string, dispatcher?: Dispatcher) {
        const iconStyle = {
            padding: "0.2em 0.4em",
            margin: "0em",
            background: "none",
            outline: "none",
            fontSize: "16px",
            border: "none",
            borderRadius: "0.2em"
        };

        const button = document.createElement("button");

        button.title = tooltip;

        style(button, iconStyle);

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            throw new Error("Could not get 2D context from canvas");
        }

        button.appendChild(canvas);

        this.ctx = ctx;
        this.dom = button;
        this.canvas = canvas;
        this.size = size;
        this.tooltip = tooltip;
        this.dispatcher = dispatcher;

        this.dpr = 1;

        if (dispatcher) {
            dispatcher.on("resize", this.resize.bind(this));
        }

        const borders = {
            border: `1px solid ${Theme.b}`
            // boxShadow: Theme.b + ' 1px 1px'
        };

        const noBorders = {
            border: "1px solid transparent"
            // boxShadow: 'none'
        };

        const normal = "none"; // Theme.b;
        const down = Theme.b;

        button.style.background = normal;
        style(button, noBorders);

        button.addEventListener("mouseover", () => {
            // button.style.background = up;
            style(button, borders);

            this.ctx.fillStyle = Theme.d;
            this.ctx.shadowColor = Theme.b;
            this.ctx.shadowBlur = 0.5 * this.dpr;
            this.ctx.shadowOffsetX = this.dpr;
            this.ctx.shadowOffsetY = this.dpr;
            this.draw();

            if (this.tooltip && this.dispatcher) {
                this.dispatcher.fire("status", this.tooltip);
            }
        });

        button.addEventListener("mousedown", () => {
            button.style.background = down;
            // ctx.fillStyle = Theme.b;
            // me.draw();
        });

        button.addEventListener("mouseup", () => {
            // ctx.fillStyle = Theme.d;
            button.style.background = normal;
            style(button, borders);
            // me.draw();
        });

        button.addEventListener("mouseout", () => {
            // ctx.fillStyle = Theme.c;

            button.style.background = normal;
            style(button, noBorders);
            this.dropshadow = false;
            this.ctx.fillStyle = Theme.c;
            this.ctx.shadowColor = null as any;
            this.ctx.shadowBlur = 0;
            this.ctx.shadowOffsetX = 0;
            this.ctx.shadowOffsetY = 0;
            this.draw();
        });

        if (icon) {
            this.setIcon(icon);
        }
    }

    resize(): void {
        this.dpr = window.devicePixelRatio;

        const height = this.size;

        if (!this.icon) {
            return;
        }

        const glyph = font.fonts[this.icon];

        this.canvas.height = height * this.dpr;
        this.canvas.style.height = `${height}px`;

        const scale = height / font.unitsPerEm;
        let width = Math.floor(glyph.advanceWidth * scale + 0.5);

        width += 2;

        this.canvas.width = width * this.dpr;
        this.canvas.style.width = `${width}px`;

        this.ctx.fillStyle = Theme.c;
        this.draw();
    }

    setSize(s: number): void {
        this.size = s;
        this.resize();
    }

    setIcon(icon: string): void {
        this.icon = icon;

        if (!font.fonts[icon]) {
            console.warn("Font icon not found!");
        }

        this.resize();
    }

    onClick(e: (event: MouseEvent) => void): void {
        this.dom.addEventListener("click", e as any);
    }

    onLongHold(f: () => void): void {
        const LONG_HOLD_DURATION = 500;
        let longHoldTimer: ReturnType<typeof setTimeout> | undefined;

        // not most elagent but oh wells.
        function startHold(e: Event) {
            e.preventDefault();
            e.stopPropagation();
            longHoldTimer = setTimeout(() => {
                if (longHoldTimer) {
                    // console.log("LONG HOLD-ED!");
                    f();
                }
            }, LONG_HOLD_DURATION);
        }

        function clearLongHoldTimer() {
            if (longHoldTimer) {
                clearTimeout(longHoldTimer);
                longHoldTimer = undefined;
            }
        }

        this.dom.addEventListener("mousedown", startHold);
        this.dom.addEventListener("touchstart", startHold);
        this.dom.addEventListener("mouseup", clearLongHoldTimer);
        this.dom.addEventListener("mouseout", clearLongHoldTimer);
        this.dom.addEventListener("touchend", clearLongHoldTimer);
    }

    setTip(tip: string): void {
        this.tooltip = tip;
        this.dom.title = tip;
    }

    draw(): void {
        if (!this.icon) {
            return;
        }

        const ctx = this.ctx;

        const glyph = font.fonts[this.icon];

        const height = this.size;
        const dpr = window.devicePixelRatio;
        const scale = (height / font.unitsPerEm) * dpr;
        const pathCommands = glyph.commands.split(" ");

        ctx.save();
        ctx.clearRect(0, 0, this.canvas.width * dpr, this.canvas.height * dpr);

        if (this.dropshadow) {
            ctx.save();
            ctx.fillStyle = Theme.b;
            ctx.translate(1.5 * dpr, 1.5 * dpr);
            ctx.scale(scale, -scale);
            ctx.translate(0, -font.ascender);
            ctx.beginPath();

            for (let i = 0, il = pathCommands.length; i < il; i++) {
                const cmds = pathCommands[i].split(",");
                const params = cmds.slice(1);

                try {
                    const cmd = IconButton.CMD_MAP[cmds[0]];

                    if (cmd && typeof (ctx as any)[cmd] === "function") {
                        (ctx as any)[cmd].apply(ctx, params);
                    }
                } catch (e: any) {
                    console.error(e.message);
                }
            }

            ctx.fill();
            ctx.restore();
        }

        ctx.scale(scale, -scale);
        ctx.translate(0, -font.ascender);
        ctx.beginPath();

        for (let i = 0, il = pathCommands.length; i < il; i++) {
            const cmds = pathCommands[i].split(",");
            const params = cmds.slice(1);

            const cmd = IconButton.CMD_MAP[cmds[0]];

            if (!cmd || typeof (ctx as any)[cmd] !== "function") {
                console.warn(`invalid path command: ${pathCommands[i]}`);
                continue;
            }

            try {
                (ctx as any)[cmd].apply(ctx, params);
            } catch (e: any) {
                console.error(e.message);
            }
        }

        ctx.fill();
        ctx.restore();

        /*
	const triangle = height / 3 * dpr;
	ctx.save();
	// ctx.translate(dpr * 2, 0);
	// ctx.fillRect(this.canvas.width - triangle, this.canvas.height - triangle, triangle, triangle);
	ctx.beginPath();
	ctx.moveTo(this.canvas.width - triangle, this.canvas.height - triangle / 2);
	ctx.lineTo(this.canvas.width, this.canvas.height - triangle / 2);
	ctx.lineTo(this.canvas.width - triangle / 2, this.canvas.height);
	ctx.fill();
	ctx.restore();
	*/
    }
}
